import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/* eslint-disable @typescript-eslint/no-explicit-any */

const TOKEN_KEY = 'yfeng:auth-token';

/** Coerce any backend error payload into a human-readable string. */
function stringifyError(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    // FastAPI/Pydantic validation errors: [{loc, msg, type, input}, ...]
    return (
      raw
        .map((e) => (e && typeof e === 'object' && 'msg' in e ? (e as any).msg : String(e)))
        .filter(Boolean)
        .join('；') || '輸入資料驗證失敗'
    );
  }
  if (raw && typeof raw === 'object') {
    const o = raw as any;
    return o.msg || o.message || o.detail || JSON.stringify(o);
  }
  return raw != null ? String(raw) : 'Network error';
}

/**
 * Single axios instance for the whole app.
 *
 * - Reads VITE_API_BASE_URL at build time
 * - Auto-attaches JWT from localStorage to each request
 * - On 401, clears the token and forces a redirect to /login
 *
 * This intentionally does NOT trigger redirect for LIFF routes — LIFF auth
 * is handled separately by `src/liff/LiffApp.tsx` using a one-time token in
 * the URL path.
 */
function createApiClient(): AxiosInstance {
  const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

  const instance = axios.create({
    baseURL,
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<any>) => {
      // Normalize error payloads to a stable shape.
      const status = error.response?.status;
      const payload = error.response?.data;
      const rawMessage =
        (typeof payload === 'object' && payload && 'error' in payload && (payload as any).error) ||
        (typeof payload === 'object' && payload && 'detail' in payload && (payload as any).detail) ||
        error.message ||
        'Network error';
      // `message` must ALWAYS be a string — FastAPI 422 returns `detail` as an
      // array of {type, loc, msg, input}. Rendering that object as a React child
      // throws React error #31 and white-screens the whole app. Flatten it.
      const message = stringifyError(rawMessage);

      if (status === 401 && !window.location.pathname.startsWith('/liff')) {
        clearAuthToken();
        if (window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
      }

      return Promise.reject({
        status,
        message,
        raw: payload,
      });
    },
  );

  return instance;
}

export const api = createApiClient();

// ---- token helpers ----

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore quota errors */
  }
}

export function clearAuthToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

// ---- typed unwrap helper ----

export interface ApiError {
  status?: number;
  message: string;
  raw?: unknown;
}

/**
 * Backends typically return a `data` envelope. This helper unwraps the
 * envelope when present, and otherwise returns the raw body.
 */
export function unwrap<T>(payload: any): T {
  if (payload && typeof payload === 'object' && 'data' in payload && payload.success !== undefined) {
    if (!payload.success) {
      throw { message: payload.error || 'Request failed', raw: payload } satisfies ApiError;
    }
    return payload.data as T;
  }
  return payload as T;
}
